import { ConstraintField, ModelConstraintsResponse } from '../../api/structs/model';
import { MediaOutputSpec } from '../../api/structs/media-spec';

// ==================== Field metadata ====================

/** Display-friendly labels for known spec fields */
const FIELD_LABELS: Record<string, string> = {
  dimensions: 'Dimensions',
  width: 'Width',
  height: 'Height',
  steps: 'Steps',
  cfg_scale: 'CFG Scale',
  duration: 'Duration (s)',
  fps: 'FPS',
  seed: 'Seed',
  scheduler: 'Scheduler',
  clip_skip: 'CLIP Skip',
  prompt_weighting: 'Prompt Weighting',
  negative_prompt: 'Negative Prompt',
  denoise: 'Denoise Strength',
  guidance: 'Guidance',
};

/** Fields that should be hidden from the settings UI */
const HIDDEN_FIELDS = new Set([
  'provider',
  'model',
  'prompt',
  'lora',
  'frameImages',
  'referenceImages',
]);

// ==================== Helpers ====================

/**
 * Get a display label for a constraint field key.
 */
export function getFieldLabel(key: string): string {
  return FIELD_LABELS[key] || key
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Check if a field should be shown in the settings UI.
 */
export function isVisibleField(key: string): boolean {
  return !HIDDEN_FIELDS.has(key);
}

/**
 * Get all visible fields from a constraints response, sorted by a sensible order.
 */
export function getVisibleFields(
  constraints: ModelConstraintsResponse
): Array<{ key: string; field: ConstraintField }> {
  if (!constraints.fields) return [];

  const ORDER = [
    'dimensions', 'width', 'height', 'steps', 'cfg_scale', 'guidance',
    'duration', 'fps', 'seed', 'scheduler', 'clip_skip',
    'prompt_weighting', 'denoise', 'negative_prompt',
  ];

  const entries = Object.entries(constraints.fields)
    .filter(([key]) => isVisibleField(key))
    .map(([key, field]) => ({ key, field }));

  entries.sort((a, b) => {
    const ai = ORDER.indexOf(a.key);
    const bi = ORDER.indexOf(b.key);
    if (ai !== -1 && bi !== -1) return ai - bi;
    if (ai !== -1) return -1;
    if (bi !== -1) return 1;
    return a.key.localeCompare(b.key);
  });

  return entries;
}

/**
 * Clamp a numeric value to the constraint's min/max.
 */
export function clampToConstraint(value: number, field: ConstraintField): number {
  let v = value;
  if (field.min !== undefined && v < field.min) v = field.min;
  if (field.max !== undefined && v > field.max) v = field.max;
  if (field.type === 'integer') v = Math.round(v);

  const multiplier = (() => {
    if (typeof field.multiplier === 'number' && field.multiplier > 0) return field.multiplier;
    if (typeof field.multiple_of === 'number' && field.multiple_of > 0) return field.multiple_of;
    if (typeof field.step === 'number' && field.step > 1 && field.type === 'integer') return field.step;
    return undefined;
  })();

  if (multiplier) {
    const min = typeof field.min === 'number' ? field.min : 0;
    const snapUnits = (v - min) / multiplier;
    const snapped =
      min +
      (field.type === 'integer'
        ? Math.floor(snapUnits)
        : Math.round(snapUnits)) * multiplier;
    v = snapped;
    if (field.min !== undefined && v < field.min) v = field.min;
    if (field.max !== undefined && v > field.max) v = field.max;
    if (field.type === 'integer') v = Math.round(v);
  }
  return v;
}

/**
 * Get a sensible default value for a constraint field.
 */
export function getFieldDefault(field: ConstraintField): unknown {
  if (field.default !== undefined) return field.default;
  if (field.enum && field.enum.length > 0) return field.enum[0];
  if (field.type === 'number' || field.type === 'integer') {
    return field.min ?? 0;
  }
  if (field.type === 'boolean') return false;
  if (field.type === 'string') return '';
  return undefined;
}

/**
 * Build a default settings object from constraints.
 */
export function buildDefaultSettings(
  constraints: ModelConstraintsResponse
): Partial<MediaOutputSpec> {
  const settings: Record<string, unknown> = {};

  if (constraints.defaults) {
    Object.assign(settings, constraints.defaults);
  }

  if (constraints.fields) {
    for (const [key, field] of Object.entries(constraints.fields)) {
      if (HIDDEN_FIELDS.has(key)) continue;
      if (settings[key] === undefined) {
        const def = getFieldDefault(field);
        if (def !== undefined) settings[key] = def;
      }
    }
  }

  // Provider settings defaults (first enum option for each setting)
  if (constraints.provider_settings) {
    const psDefaults: Record<string, unknown> = {};
    for (const [providerKey, providerSchema] of Object.entries(constraints.provider_settings)) {
      if (typeof providerSchema !== 'object' || providerSchema === null) continue;
      const providerDefaults: Record<string, unknown> = {};
      for (const [settingKey, options] of Object.entries(providerSchema as Record<string, unknown>)) {
        if (Array.isArray(options) && options.length > 0) {
          providerDefaults[settingKey] = options[0];
        }
      }
      if (Object.keys(providerDefaults).length > 0) {
        psDefaults[providerKey] = providerDefaults;
      }
    }
    if (Object.keys(psDefaults).length > 0) {
      settings.provider_settings = psDefaults;
    }
  }

  return settings as Partial<MediaOutputSpec>;
}

/**
 * Validate and clamp current settings against constraints.
 * Returns a new settings object with out-of-range values fixed.
 */
export function validateSettings(
  settings: Partial<MediaOutputSpec>,
  constraints: ModelConstraintsResponse
): Partial<MediaOutputSpec> {
  const validKeys = new Set(Object.keys(constraints.fields ?? {}));
  const result: Record<string, unknown> = {};

  // Keep known constraint fields and provider_settings
  for (const [key, value] of Object.entries(settings)) {
    if (!validKeys.has(key) && key !== 'provider_settings') continue;
    result[key] = value;
  }

  // Clamp and validate constraint field values
  if (constraints.fields) {
    for (const [key, field] of Object.entries(constraints.fields)) {
      const value = result[key];
      if (value === undefined) continue;

      if (
        (field.type === 'number' || field.type === 'integer') &&
        typeof value === 'number'
      ) {
        result[key] = clampToConstraint(value, field);
      }

      if (field.enum && !field.enum.includes(value as string | number | boolean)) {
        result[key] = field.enum[0];
      }
    }
  }

  // Validate provider_settings against schema
  if (constraints.provider_settings && result.provider_settings) {
    const ps = result.provider_settings as Record<string, unknown>;
    const validatedPs: Record<string, unknown> = {};
    for (const [providerKey, providerSchema] of Object.entries(constraints.provider_settings as Record<string, unknown>)) {
      if (typeof providerSchema !== 'object' || providerSchema === null) continue;
      const providerPs = ps[providerKey] as Record<string, unknown> | undefined;
      const validatedProvider: Record<string, unknown> = {};
      for (const [settingKey, options] of Object.entries(providerSchema as Record<string, unknown>)) {
        if (!Array.isArray(options)) continue;
        const currentValue = providerPs?.[settingKey];
        if (options.includes(currentValue)) {
          validatedProvider[settingKey] = currentValue;
        } else if (options.length > 0) {
          validatedProvider[settingKey] = options[0];
        }
      }
      if (Object.keys(validatedProvider).length > 0) {
        validatedPs[providerKey] = validatedProvider;
      }
    }
    result.provider_settings = validatedPs;
  }

  return result as Partial<MediaOutputSpec>;
}

/**
 * Convert a saved MediaOutputSpec back into modal-friendly settings.
 * When a model exposes a dimensions enum, prefer hydrating that field from width/height.
 */
export function outputSpecToSettings(
  settings: Partial<MediaOutputSpec>,
  constraints: ModelConstraintsResponse
): Partial<MediaOutputSpec> {
  const next = { ...settings } as Record<string, unknown>;
  const dimensionsField = constraints.fields?.dimensions;

  if (dimensionsField) {
    const width = typeof next.width === 'number' ? next.width : undefined;
    const height = typeof next.height === 'number' ? next.height : undefined;
    const explicitDimensions =
      typeof next.dimensions === 'string' && next.dimensions.trim() ? next.dimensions.trim() : '';
    const derivedDimensions =
      width && height ? `${width}x${height}` : '';

    if (!explicitDimensions && derivedDimensions) {
      next.dimensions = derivedDimensions;
    }

    delete next.width;
    delete next.height;
  }

  return next as Partial<MediaOutputSpec>;
}

/**
 * Split a "WxH" dimensions value into { width, height }.
 * Returns undefined if the value is not a valid dimensions string.
 */
export function parseDimensions(value: unknown): { width: number; height: number } | undefined {
  if (typeof value !== 'string') return undefined;
  const parts = value.split('x');
  if (parts.length !== 2) return undefined;
  const width = parseInt(parts[0], 10);
  const height = parseInt(parts[1], 10);
  if (isNaN(width) || isNaN(height)) return undefined;
  return { width, height };
}

/**
 * Convert local settings (which may contain "dimensions" string) into
 * a MediaOutputSpec-ready object with numeric width/height.
 */
export function settingsToOutputSpec(
  settings: Partial<MediaOutputSpec>
): Partial<MediaOutputSpec> {
  const { dimensions, provider_settings, ...rest } = settings as Record<string, unknown>;
  const out: Record<string, unknown> = {};

  // Only include meaningful values — skip noise defaults
  for (const [key, value] of Object.entries(rest)) {
    if (value === undefined || value === null) continue;
    if (value === '' && key === 'negative_prompt') continue;
    if (value === 0 && key === 'seed') continue;
    out[key] = value;
  }

  if (dimensions) {
    const parsed = parseDimensions(dimensions);
    if (parsed) {
      out.width = parsed.width;
      out.height = parsed.height;
    }
  }

  if (provider_settings && Object.keys(provider_settings).length > 0) {
    out.provider_settings = provider_settings;
  }

  return out as Partial<MediaOutputSpec>;
}
